import type { JSX } from 'react'
import { useBrand } from '@/brand/context'

/**
 * The wordmark — AugRank's, or the organisation's on its own host.
 *
 * On the apex this is the approved brand lockup (design/brand/BRAND.md). The
 * mark is served from `/design/`, the same static copy of the design layer the
 * tokens and the Atlas stylesheet come from (`scripts/sync-design.sh`), so
 * there is one copy of the SVG in the repository rather than one per workspace.
 *
 * `alt=""` on the mark and the word beside it as real text: the two together
 * are one lockup, and reading "AugRank AugRank" is what an alt on both gives a
 * screen reader.
 *
 * On a tenant host it is the organisation's logo when one is uploaded, with the
 * organisation's name as its `alt` — the logo *is* the name here, there is no
 * word beside it — and the name set as the wordmark when there is none. The
 * word is the same class the apex word wears, so it sits in the rail at the
 * same size, and it carries no `<em>`: the orange half of *AugRank* is
 * AugRank's, not the tenant's.
 */
export function Lockup({ size = '1.75rem' }: { size?: string }): JSX.Element {
  const brand = useBrand()

  if (brand.host === 'tenant') {
    const name = brand.name ?? brand.productName
    if (brand.logoUrl) {
      return (
        <img
          className="app-lockup__logo"
          src={brand.logoUrl}
          alt={name}
          style={{ height: size, width: 'auto' }}
        />
      )
    }
    return <span className="app-side__word">{name}</span>
  }

  return (
    <>
      {/* `width`/`height` are the mark's 92×116 viewBox, so the browser reserves
          the box before the SVG arrives instead of shifting the lockup's word
          when it does (Lighthouse `unsized-images`, and the 0.18 CLS it caused at
          390px). The rendered size is still `size`: CSS wins over the attribute
          for height, and `width: auto` keeps the ratio. */}
      <img
        src="/design/augrank-mark.svg"
        alt=""
        width={92}
        height={116}
        style={{ height: size, width: 'auto' }}
      />
      <span className="app-side__word">
        Aug<em>Rank</em>
      </span>
    </>
  )
}
